import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useLabClients } from 'hooks/useLabClients';
import { ClientType, LabClient } from 'models/client-directory';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CustomerDropDownProps = {
    onCustomerSelect: (customer: LabClient | null) => void;
    selectedCustomerId?: string | null;
    clientType: ClientType;
};

const CustomerDropDown: FC<CustomerDropDownProps> = ({
    onCustomerSelect,
    selectedCustomerId,
    clientType,
}: CustomerDropDownProps) => {
    const { t: translate } = useTranslation(['common']);

    const [labClients, setLabClients] = useState<LabClient[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<LabClient | null>(null);

    const { getLabClients, isLabClientListLoading } = useLabClients(setLabClients, clientType);

    // DD-3846: a lab client whose counterpart organization is gone (deleted clinic/lab) has no
    // customer/lab to show or select; rendering it crashed the orders page.
    const options = (labClients || []).filter((client) =>
        clientType === ClientType.CUSTOMER ? !!client?.customer?._id : !!client?.lab?._id,
    );

    useEffect(() => {
        if (labClients) {
            const selectedCustomer =
                clientType === ClientType.CUSTOMER
                    ? labClients.find((client) => client?.customer?._id === selectedCustomerId)
                    : labClients.find((client) => client?.lab?._id === selectedCustomerId);
            setSelectedCustomer(selectedCustomer || null);
        }
    }, [selectedCustomerId, labClients]);

    useEffect(() => {
        getLabClients({ variables: { offset: 0, limit: 0 } });
    }, []);

    return (
        <FormControl fullWidth>
            <Autocomplete
                id={clientType === ClientType.SUPPLIER ? 'supplier-select' : 'clinic-select'}
                options={options}
                getOptionLabel={(labClient: LabClient) => {
                    if (!labClient) return '';
                    if (clientType === ClientType.CUSTOMER) {
                        return labClient.customer?.name || '';
                    }
                    return labClient.lab?.name || '';
                }}
                renderOption={(props, option: LabClient) => {
                    if (clientType === ClientType.CUSTOMER) {
                        return <li {...{ ...props, key: option.customer?._id }}>{option.customer?.name}</li>;
                    }
                    return <li {...{ ...props, key: option.lab?._id }}>{option.lab?.name}</li>;
                }}
                isOptionEqualToValue={(option: LabClient, value: LabClient) => {
                    return clientType === ClientType.CUSTOMER
                        ? option?.customer?._id === value?.customer?._id
                        : option?.lab?._id === value?.lab?._id;
                }}
                value={selectedCustomer || null}
                onChange={(_, value) => onCustomerSelect(value)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={
                            clientType === ClientType.CUSTOMER
                                ? translate('common:CUSTOMER')
                                : translate('common:SUPPLIER')
                        }
                    />
                )}
                clearOnEscape
                clearOnBlur={false}
                disableClearable={false}
                disabled={isLabClientListLoading}
            />
        </FormControl>
    );
};

export default CustomerDropDown;
